import React, { useEffect, useMemo, useState } from "react"
import { ui } from "../../../shared/styles/uiSystem"
import { getPublicPlans } from "../services/subscriptionsService"
import {
  getFeatureConfig,
  getPlanLabel,
  resolveRequiredPlanFromPlans
} from "../utils/planPresentation"

let PUBLIC_PLANS_CACHE = null
let PUBLIC_PLANS_PROMISE = null

function sortPlans(plans) {
  return [...(plans || [])].sort(
    (a, b) => Number(a.displayOrder || 0) - Number(b.displayOrder || 0)
  )
}

async function loadPublicPlansCached() {
  if (Array.isArray(PUBLIC_PLANS_CACHE)) {
    return PUBLIC_PLANS_CACHE
  }

  if (!PUBLIC_PLANS_PROMISE) {
    PUBLIC_PLANS_PROMISE = getPublicPlans()
      .then((plans) => {
        PUBLIC_PLANS_CACHE = Array.isArray(plans) ? sortPlans(plans) : []
        return PUBLIC_PLANS_CACHE
      })
      .catch(() => {
        PUBLIC_PLANS_CACHE = []
        return PUBLIC_PLANS_CACHE
      })
      .finally(() => {
        PUBLIC_PLANS_PROMISE = null
      })
  }

  return PUBLIC_PLANS_PROMISE
}

function resolveRecommendedPlanCode({
  featureKey,
  requiredPlan,
  recommendedPlan,
  plans
}) {
  if (requiredPlan) return requiredPlan
  if (recommendedPlan?.code) return recommendedPlan.code
  if (recommendedPlan?.name) return recommendedPlan.name

  const resolved = resolveRequiredPlanFromPlans(featureKey, plans || [])
  return resolved || "Plan superior"
}

function resolveMeta({
  featureKey,
  title,
  subtitle,
  requiredPlanCode,
  bullets
}) {
  const config = getFeatureConfig(featureKey) || {}

  const resolvedPlanLabel =
    requiredPlanCode && requiredPlanCode !== "Plan superior"
      ? getPlanLabel(requiredPlanCode)
      : "Plan superior"

  return {
    title: title || config.title || "Función premium",
    subtitle: subtitle || "",
    requiredPlanCode,
    requiredPlanLabel: resolvedPlanLabel,
    bullets:
      Array.isArray(bullets) && bullets.length
        ? bullets
        : Array.isArray(config.bullets)
          ? config.bullets
          : []
  }
}

export default function SubscriptionFeatureAccess({
  enabled = true,
  variant = "overlay",
  featureKey = "",
  title = "",
  subtitle = "",
  requiredPlan = "",
  recommendedPlan = null,
  bullets = [],
  planName = "",
  onUpgrade,
  buttonLabel = "",
  compact = false,
  minHeight = 360,
  children
}) {
  const [dynamicRequiredPlan, setDynamicRequiredPlan] = useState(
    requiredPlan || recommendedPlan?.code || recommendedPlan?.name || ""
  )

  useEffect(() => {
    let cancelled = false

    const explicitRequiredPlan =
      requiredPlan || recommendedPlan?.code || recommendedPlan?.name || ""

    if (explicitRequiredPlan) {
      setDynamicRequiredPlan(explicitRequiredPlan)
      return
    }

    if (enabled) return

    loadPublicPlansCached().then((plans) => {
      if (cancelled) return
      const resolved = resolveRecommendedPlanCode({
        featureKey,
        requiredPlan,
        recommendedPlan,
        plans
      })
      setDynamicRequiredPlan(resolved)
    })

    return () => {
      cancelled = true
    }
  }, [enabled, featureKey, requiredPlan, recommendedPlan])

  const meta = useMemo(() => {
    return resolveMeta({
      featureKey,
      title,
      subtitle,
      requiredPlanCode: dynamicRequiredPlan,
      bullets
    })
  }, [featureKey, title, subtitle, dynamicRequiredPlan, bullets])

  const resolvedButtonLabel =
    buttonLabel || (variant === "banner" ? "Ver planes" : "Ver planes y actualizar")

  const defaultSubtitle =
    meta.requiredPlanLabel && meta.requiredPlanLabel !== "Plan superior"
      ? `Disponible desde ${meta.requiredPlanLabel}.`
      : "Disponible en un plan superior."

  if (variant === "banner") {
    if (enabled) return null

    return (
      <div
        style={{
          ...ui.infoCard,
          padding: compact ? 12 : 14,
          border: "1px solid #c7d2fe",
          background: "linear-gradient(135deg, #eef2ff, #eff6ff)"
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap"
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 900, color: "#1e3a8a" }}>
              {meta.title}
            </div>

            <div
              style={{
                fontSize: 13,
                color: "#475569",
                marginTop: 4,
                lineHeight: 1.45
              }}
            >
              {meta.subtitle || defaultSubtitle}
            </div>
          </div>

          <button
            onClick={onUpgrade}
            style={ui.primaryButton}
            disabled={!onUpgrade}
          >
            {resolvedButtonLabel}
          </button>
        </div>
      </div>
    )
  }

  if (enabled) return children

  return (
    <div
      style={{
        position: "relative",
        minHeight
      }}
    >
      <div
        style={{
          filter: "blur(6px)",
          opacity: 0.72,
          pointerEvents: "none",
          userSelect: "none"
        }}
      >
        {children}
      </div>

      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "grid",
          placeItems: "center",
          padding: 16
        }}
      >
        <div
          style={{
            ...ui.card,
            maxWidth: 520,
            width: "100%",
            padding: 18,
            textAlign: "center",
            boxShadow: "0 18px 36px rgba(15,23,42,0.18)",
            border: "1px solid #c7d2fe"
          }}
        >
          <div
            style={{
              display: "inline-flex",
              padding: "6px 10px",
              borderRadius: 999,
              background: "#eef2ff",
              border: "1px solid #c7d2fe",
              color: "#3730a3",
              fontSize: 10,
              fontWeight: 900,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              marginBottom: 10
            }}
          >
            Función premium
          </div>

          <div
            style={{
              fontSize: 18,
              fontWeight: 900,
              color: "#0f172a",
              lineHeight: 1.1
            }}
          >
            {meta.title}
          </div>

          <div
            style={{
              fontSize: 13,
              color: "#475569",
              marginTop: 8,
              lineHeight: 1.5
            }}
          >
            {meta.subtitle || defaultSubtitle}
          </div>

          <div
            style={{
              marginTop: 10,
              display: "inline-flex",
              padding: "6px 10px",
              borderRadius: 999,
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              color: "#166534",
              fontSize: 12,
              fontWeight: 800
            }}
          >
            Disponible desde {meta.requiredPlanLabel}
          </div>

          {!!planName && (
            <div
              style={{
                marginTop: 10,
                fontSize: 12,
                color: "#64748b"
              }}
            >
              Plan actual: <strong>{planName}</strong>
            </div>
          )}

          {!!meta.bullets.length && (
            <div
              style={{
                marginTop: 14,
                textAlign: "left",
                display: "grid",
                gap: 6
              }}
            >
              {meta.bullets.map((item, index) => (
                <div
                  key={index}
                  style={{
                    fontSize: 13,
                    color: "#334155",
                    lineHeight: 1.4
                  }}
                >
                  • {item}
                </div>
              ))}
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 8,
              flexWrap: "wrap",
              marginTop: 14
            }}
          >
            <button
              onClick={onUpgrade}
              style={ui.primaryButton}
              disabled={!onUpgrade}
            >
              {resolvedButtonLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}